import { useState } from 'react'

import { errorMessage } from '@/api/client'
import { useBatchApprove, useClearBdGate, useDigest, useQueue } from '@/api/queue'
import { lifecycleLabel, type Digest, type QueueRow } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ScreenHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  cn,
  useToast,
} from '@/components/ui'
import { useRouter } from '@/routes'

import { RunErrorCard } from '@/screens/Case/runParts'

/**
 * Queue — chunk 10, the docket's own "Queue lanes": batch approval with
 * lanes, SLA clocks counting to the *verified* deadline, the BD gate
 * (REQ-8), and a daily digest. Everything here reads
 * `server/app/services/queue.py`'s answer and draws it; nothing on this
 * screen decides a lane, an SLA number, or a gate state a second way — the
 * second gate is always the lenient one (I4), and that applies to a lane
 * assignment exactly as much as to `exportable`.
 *
 * ## Two lanes, three sections
 *
 * The server sends exactly two lanes, `ready` and `flagged`
 * (`GET /api/queue`). This screen splits `flagged` once more, client-side,
 * by a field the server already sent (`pack_approved`) — never by deciding
 * anything new: a flagged-and-approved matter is "approved, awaiting
 * dispatch" (informational), and a flagged-and-unapproved one is "needs
 * attention" (the reason is read straight off the row's own booleans, in
 * priority order, by `flaggedReason()` below — again commentary on an
 * already-decided fact, not a second decision).
 *
 * ## Declines are not on this screen
 *
 * `GET /api/queue` excludes every terminal-lifecycle matter, declines
 * included — a declined matter is a **pass**, done, and does not belong in
 * an active work queue (`counts_as_pass()`, `server/app/services/decline.py`).
 * The digest card below still counts declines, explicitly as `passed`,
 * never as a failure — there is no failure count anywhere on this screen,
 * because the server's own `DigestCounts` has no field for one.
 */
export function QueueScreen() {
  const queue = useQueue()
  const digest = useDigest()
  const { hasScope, isAdmin } = useAuth()
  const canWrite = hasScope('matters:write')
  // Who opened each matter. Every row carries it, but a non-admin's queue is
  // their own docket by construction (server/app/auth/ownership.py), so the
  // column would say their own name down the page. It is the admin's view of
  // who is building what, and only theirs.
  const showOwner = isAdmin
  const router = useRouter()

  const rows = queue.data?.rows ?? []
  const ready = rows.filter((row) => row.lane === 'ready')
  const flagged = rows.filter((row) => row.lane === 'flagged')
  const awaitingDispatch = flagged.filter((row) => row.pack_approved)
  const needsAttention = flagged.filter((row) => !row.pack_approved)

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        kicker="Open matters"
        title="Queue"
        description="Every active matter is either ready to approve or flagged with the reason. Declined matters are a pass and are not shown here — see the digest below."
        actions={
          <Button variant="primary" onClick={() => router.navigate('/new')}>
            New case
          </Button>
        }
      />

      <DigestCard digest={digest.data} isPending={digest.isPending} isError={digest.isError} error={digest.error} />

      {queue.isPending ? (
        <Card>
          <p className="m-0 text-body text-muted">Loading the queue…</p>
        </Card>
      ) : queue.isError ? (
        <RunErrorCard title="Could not load the queue" error={queue.error} />
      ) : (
        <>
          <ReadyLane rows={ready} canWrite={canWrite} showOwner={showOwner} />
          <FlaggedLane
            showOwner={showOwner}
            title="Needs attention"
            rows={needsAttention}
            empty="Nothing is flagged. Every active matter is either ready to approve or already approved."
          />
          <FlaggedLane
            showOwner={showOwner}
            title="Approved — awaiting dispatch"
            rows={awaitingDispatch}
            description="Approved in a batch or one at a time. Dispatch is still done matter by matter, from the assessment pack on each case — this screen does not send anything."
            empty="Nothing is waiting. An approved matter stays here until its assessment pack is dispatched."
          />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Digest
// ---------------------------------------------------------------------------

function DigestCard({
  digest,
  isPending,
  isError,
  error,
}: {
  digest: Digest | undefined
  isPending: boolean
  isError: boolean
  error: unknown
}) {
  return (
    <Card role="group" aria-label="Digest" className="gap-3">
      <CardHeader
        title="Today's digest"
        description={digest ? `As of ${digest.date}.` : undefined}
      />
      {isError ? (
        <RunErrorCard title="Could not load today's digest" error={error} flush />
      ) : isPending || !digest ? (
        <p className="m-0 text-body text-muted">Loading…</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Ready" value={digest.counts.ready} tone="ok" />
          <Stat label="Flagged" value={digest.counts.flagged} tone="warn" />
          <Stat label="Waiting on business sign-off" value={digest.counts.bd_gate_pending} tone="block" />
          <Stat label="SLA overdue" value={digest.counts.sla_overdue} tone="block" />
          <Stat label="SLA due soon" value={digest.counts.sla_due_soon} tone="warn" />
          <Stat label="Declined (pass)" value={digest.counts.declined} tone="neutral" />
          <Stat label="Dispatched" value={digest.counts.dispatched} tone="ok" />
          <Stat label="Passed total" value={digest.counts.passed} tone="ok" />
        </div>
      )}
    </Card>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'ok' | 'warn' | 'block' | 'neutral'
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-divider bg-ground px-3 py-2">
      <span className="text-micro text-muted">{label}</span>
      <span className={cn('text-h2 font-semibold', TONE_TEXT[tone])}>{value}</span>
    </div>
  )
}

const TONE_TEXT: Record<'ok' | 'warn' | 'block' | 'neutral', string> = {
  ok: 'text-ok-fg',
  warn: 'text-warn-fg',
  block: 'text-block-fg',
  neutral: 'text-muted',
}

// ---------------------------------------------------------------------------
// Empty lanes
// ---------------------------------------------------------------------------

/**
 * A lane with no rows, as one slim row instead of a card with a heading and a
 * sentence under it — three empty lanes on a quiet day were a page of white
 * boxes. Same `role="group"` and accessible name as the full lane, so a screen
 * reader still finds the lane by its title and says that it is empty.
 *
 * Padding is set here (`flush` clears the card's own) rather than layered over
 * `Card`'s default: `cn` only joins class names, so two `p-*` utilities on one
 * element would be settled by stylesheet order, not by intent.
 */
function EmptyLane({ title, empty }: { title: string; empty: string }) {
  return (
    <Card flush role="group" aria-label={title} className="px-[18px] py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h5 className="m-0">{title}</h5>
        <span className="text-meta tabular-nums text-muted">0</span>
        <p className="m-0 text-meta text-muted">{empty}</p>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Ready lane — batch approval
// ---------------------------------------------------------------------------

/**
 * Why a matter was left out of a batch approval, in words. The server sends a
 * short code per skipped matter (`approve_batch`, `server/app/services/queue.py`);
 * a code nobody here has a sentence for is shown as sent, so a reason the
 * server adds later is never swallowed.
 */
const SKIP_REASON_LABELS: Record<string, string> = {
  matter_not_found: 'matter not found',
  already_approved: 'already approved',
  bd_gate_pending: 'waiting on business sign-off',
  matter_terminal: 'the matter is closed or declined',
  no_rendered_pack: 'no assessment pack has been prepared yet',
  deadline_not_verified: 'its deadline has not been verified',
}

function skipReasonLabel(reason: string): string {
  return Object.prototype.hasOwnProperty.call(SKIP_REASON_LABELS, reason)
    ? (SKIP_REASON_LABELS[reason] as string)
    : reason
}

function ReadyLane({
  rows,
  canWrite,
  showOwner,
}: {
  rows: QueueRow[]
  canWrite: boolean
  showOwner: boolean
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const batchApprove = useBatchApprove()
  const toast = useToast()
  const router = useRouter()

  function toggle(matterId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(matterId)) next.delete(matterId)
      else next.add(matterId)
      return next
    })
  }

  function toggleAll() {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.matter_id))))
  }

  async function handleApprove() {
    const matterIds = [...selected]
    try {
      const result = await batchApprove.mutateAsync(matterIds)
      setSelected(new Set())
      if (result.skipped.length === 0) {
        toast.push(`Approved ${result.approved.length} matter(s).`, { tone: 'success' })
      } else {
        toast.push(
          `Approved ${result.approved.length} of ${matterIds.length} — ${result.skipped.length} skipped.`,
          {
            tone: result.approved.length > 0 ? 'info' : 'error',
            description: result.skipped
              .map((s) => `${s.matter_id}: ${skipReasonLabel(s.reason)}`)
              .join('; '),
            duration: 0,
          },
        )
      }
    } catch (error) {
      toast.push('Batch approval failed.', { tone: 'error', description: errorMessage(error) })
    }
  }

  if (rows.length === 0) {
    return <EmptyLane title="Ready" empty="Nothing is ready to approve right now." />
  }

  return (
    <Card role="group" aria-label="Ready" className="gap-3">
      <CardHeader
        title="Ready"
        description="Gate-clean, deadline verified, business sign-off given, not yet approved."
        actions={
          canWrite ? (
            <Button
              variant="primary"
              size="sm"
              disabled={selected.size === 0}
              loading={batchApprove.isPending}
              loadingLabel="Approving…"
              onClick={() => void handleApprove()}
            >
              Approve {selected.size > 0 ? `(${selected.size})` : 'selected'}
            </Button>
          ) : null
        }
      />

      <Table>
        <THead>
          <TR>
            {canWrite ? (
              <TH>
                <input
                  type="checkbox"
                  aria-label="Select all ready matters"
                  checked={selected.size === rows.length}
                  onChange={toggleAll}
                />
              </TH>
            ) : null}
            <TH>Matter</TH>
            <TH>Office</TH>
            <TH>Status</TH>
            {showOwner ? <TH>Owner</TH> : null}
            <TH>SLA</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => (
            <TR key={row.matter_id}>
              {canWrite ? (
                <TD>
                  <input
                    type="checkbox"
                    aria-label={`Select ${row.matter_id}`}
                    checked={selected.has(row.matter_id)}
                    onChange={() => toggle(row.matter_id)}
                  />
                </TD>
              ) : null}
              <TD>
                <a
                  href={router.href(`/case/${row.matter_id}`)}
                  className="font-mono text-meta text-brand-700 no-underline hover:underline"
                >
                  {row.matter_id}
                </a>
              </TD>
              <TD>
                <Badge tone="outline">{row.jurisdiction}</Badge>
              </TD>
              <TD meta>{lifecycleLabel(row.lifecycle)}</TD>
              {showOwner ? <TD meta>{row.owner_actor ?? '—'}</TD> : null}
              <TD>
                <SlaBadge row={row} />
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Flagged — needs attention / approved-awaiting-dispatch
// ---------------------------------------------------------------------------

/**
 * Why one row landed in `flagged`, read off the row's own already-decided
 * booleans in priority order. Commentary, not a decision — the row is
 * flagged because the server said so; this only explains which of the
 * reasons that could be true actually is, first match wins.
 */
function flaggedReason(row: QueueRow): string {
  if (row.pack_approved) return 'approved — awaiting dispatch'
  if (row.bd_gate_pending) return 'Waiting on business sign-off'
  if (!row.deadline_verified) return 'deadline not verified'
  if (!row.exportable) return 'no clean draft to approve'
  return 'needs review'
}

function FlaggedLane({
  title,
  rows,
  description,
  empty,
  showOwner,
}: {
  title: string
  rows: QueueRow[]
  description?: string
  /** The admin's "who is building what" column — see `QueueScreen`. */
  showOwner: boolean
  /** What to say when the lane is empty. Required, and lane-specific on
   * purpose: "Nothing here." is true of every lane and tells a reviewer
   * nothing about which one they are looking at or what would put a matter
   * in it. */
  empty: string
}) {
  const { isBd, isAdmin } = useAuth()
  const canClearBdGate = isBd || isAdmin
  const clearBdGate = useClearBdGate()
  const toast = useToast()
  const router = useRouter()

  async function handleClear(matterId: string) {
    try {
      await clearBdGate.mutateAsync(matterId)
      toast.push(`Business sign-off given for ${matterId}.`, { tone: 'success' })
    } catch (error) {
      toast.push('Could not record the business sign-off.', { tone: 'error', description: errorMessage(error) })
    }
  }

  if (rows.length === 0) {
    return <EmptyLane title={title} empty={empty} />
  }

  return (
    <Card role="group" aria-label={title} className="gap-3">
      <CardHeader title={title} description={description} />

      <Table>
        <THead>
          <TR>
            <TH>Matter</TH>
            <TH>Office</TH>
            <TH>Reason</TH>
            {showOwner ? <TH>Owner</TH> : null}
            <TH>SLA</TH>
            <TH></TH>
          </TR>
        </THead>
        <TBody>
          {
            // No `TableEmpty` branch: the empty case returned above, so one
            // here would be a second answer to the same question that nobody
            // could ever see — and the two would drift.
            rows.map((row) => (
              <TR key={row.matter_id}>
                <TD>
                  <a
                    href={router.href(`/case/${row.matter_id}`)}
                    className="font-mono text-meta text-brand-700 no-underline hover:underline"
                  >
                    {row.matter_id}
                  </a>
                </TD>
                <TD>
                  <Badge tone="outline">{row.jurisdiction}</Badge>
                </TD>
                <TD meta>
                  <Badge tone={row.pack_approved ? 'ok' : row.bd_gate_pending ? 'block' : 'warn'}>
                    {flaggedReason(row)}
                  </Badge>
                </TD>
                {showOwner ? <TD meta>{row.owner_actor ?? '—'}</TD> : null}
                <TD>
                  <SlaBadge row={row} />
                </TD>
                <TD>
                  {row.bd_gate_pending && canClearBdGate ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={clearBdGate.isPending}
                      loadingLabel="Recording…"
                      onClick={() => void handleClear(row.matter_id)}
                    >
                      Give business sign-off
                    </Button>
                  ) : null}
                </TD>
              </TR>
            ))
          }
        </TBody>
      </Table>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

/**
 * The SLA clock, read verbatim off `sla_days_remaining` — never recomputed
 * from `deadline_date` here. `null` means no verified deadline, drawn
 * plainly rather than as a countdown to an unattested date.
 */
function SlaBadge({ row }: { row: QueueRow }) {
  if (row.sla_days_remaining === null) {
    return <Badge tone="neutral">no verified deadline</Badge>
  }
  if (row.sla_days_remaining < 0) {
    return <Badge tone="block">{Math.abs(row.sla_days_remaining)}d overdue</Badge>
  }
  if (row.sla_days_remaining <= 7) {
    return <Badge tone="warn">{row.sla_days_remaining}d left</Badge>
  }
  return <Badge tone="ok">{row.sla_days_remaining}d left</Badge>
}

export default QueueScreen
